import {
  SelectionManagerProvider,
  useSelectionManager
} from "@prismicio/editor-fields"
import {
  AnimatedElement,
  Box,
  DefaultErrorMessage,
  dialogErrorImage,
  Text
} from "@prismicio/editor-ui"
import { type ReactNode, useEffect, useState } from "react"

import { BulkActionsProvider } from "../BulkActions/BulkActionsProvider"
import {
  isStandardTaskRecord,
  type TaskRecord
} from "../BulkActions/bulkActionsService"
import { Onboarding } from "../Onboarding/Onboarding"
import { useSearchDocumentParams } from "../useDocumentList"
import { ActionBar } from "./ActionBar"
import { ActionBarSkeleton } from "./ActionBar/ActionBar"
import { CreateNewDocumentModal } from "./CreateNewDocumentDialog"
import {
  DocumentListLoadingTable,
  DocumentListTable
} from "./DocumentListTable"
import { useDocumentListData } from "./useDocumentListData"
import { useHasCustomTypes } from "./useHasCustomTypes"
import { useScrolledToPageBottom } from "./useScrolledToPageBottom"

interface DocumentListBodyProps {
  tab: "working" | "archived" | "upcoming"
  /**
   * The release id to filter the documents by.
   * If not provided, the documents will be filtered by the current release.
   *
   * This also determines if the create new document button should link the new document to the release.
   *
   * Only works for the "upcoming" tab.
   */
  releaseId?: string
  createEnabled?: boolean
}

export function DocumentListBody(props: DocumentListBodyProps) {
  const { tab, releaseId, createEnabled } = props

  const [modalOpen, setModalOpen] = useState(false)

  useClearSelectionOnTabChange({ tab })

  const {
    data: { documents },
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage
  } = useDocumentListData({
    tab,
    releaseId,
    enableRevalidationToast: true
  })

  useScrolledToPageBottom(() => hasNextPage && !isFetching && fetchNextPage())

  const { hasFiltersApplied } = useSearchDocumentParams()

  const hasDocuments = documents.length > 0

  // Note: we want both to show the ActionBar when there are documents but also
  // when the user applied some filters and returned no results
  const showActionBar = hasDocuments || hasFiltersApplied

  const hasNoCustomTypes = !useHasCustomTypes()

  if (hasNoCustomTypes) return <Onboarding />

  return (
    <>
      {showActionBar && (
        <ActionBar
          tab={tab}
          setModalOpen={setModalOpen}
          createEnabled={createEnabled}
          releaseId={releaseId}
        />
      )}

      <AnimatedTable tab={tab}>
        <DocumentListTable
          tab={tab}
          setModalOpen={setModalOpen}
          documents={documents}
        />
      </AnimatedTable>

      <AnimatedElement animation={{ name: "fade" }}>
        {isFetchingNextPage && (
          <Text align="center" color="grey11" sx={{ marginBlock: 8 }}>
            Loading more results ...
          </Text>
        )}
      </AnimatedElement>

      <CreateNewDocumentModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        releaseId={tab === "upcoming" && releaseId ? releaseId : undefined}
      />
    </>
  )
}

interface DocumentListBodyLayoutProps {
  children: ReactNode
}

export function DocumentListBodyLayout(props: DocumentListBodyLayoutProps) {
  const { children } = props

  return (
    <Box
      flexDirection="column"
      gap={24}
      padding={{ left: 24, top: 24, bottom: 24 }}
      width="100%"
    >
      <SelectionManagerProvider>
        <BulkActionsProviderWithSelectionCleanup>
          {children}
        </BulkActionsProviderWithSelectionCleanup>
      </SelectionManagerProvider>
    </Box>
  )
}

interface AnimatedTableProps {
  tab: "working" | "archived" | "upcoming"
  children: ReactNode
}

function AnimatedTable(props: AnimatedTableProps) {
  const { tab, children } = props

  // It looks awful with a positive exit duration as the scroll to top occurs before the animation kicks in,
  // which makes no sense in terms of continuity.
  const exitDuration = 0

  return (
    <AnimatedElement
      animation={{ name: "slide", offset: -16 }}
      enterDuration={300}
      exitDuration={exitDuration}
    >
      <Box key={tab}>{children}</Box>
    </AnimatedElement>
  )
}

export function renderDocumentListError() {
  return (
    <Box
      height="100%"
      width="100%"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <DefaultErrorMessage
        title="Request failed"
        description="An error occurred while fetching documents."
        errorImageSrc={dialogErrorImage}
      />
    </Box>
  )
}

interface UseSelectionOnTabChangeArgs {
  tab: "working" | "archived" | "upcoming"
}

function useClearSelectionOnTabChange(args: UseSelectionOnTabChangeArgs) {
  const { tab } = args

  const { clearSelection } = useSelectionManager()
  useEffect(() => {
    clearSelection()
  }, [tab, clearSelection])
}

interface BulkActionsProviderWithSelectionCleanupProps {
  children: ReactNode
}

function BulkActionsProviderWithSelectionCleanup(
  props: BulkActionsProviderWithSelectionCleanupProps
) {
  const { children } = props

  const selectionManager = useSelectionManager()

  function onCompletion(task: TaskRecord) {
    // we have to check that the item is indeed a task document to be able to
    // get the document id the bulk action was run against, since we are
    // storing documentIds in our selection manager
    // Failed items can either be a task document or task locale

    const failedDocumentIds = task.failed.flatMap((error) => {
      return isStandardTaskRecord(error) ? error.documentId : []
    })

    task.items.forEach(
      (item) =>
        isStandardTaskRecord(item) &&
        !failedDocumentIds.includes(item.documentId) &&
        selectionManager.toggleSelection({ id: item.documentId })
    )
  }

  return (
    <BulkActionsProvider onCompletion={onCompletion}>
      {children}
    </BulkActionsProvider>
  )
}

interface DocumentListSkeletonProps {
  tab: "working" | "archived" | "upcoming"
  createEnabled?: boolean
  releaseId?: string
}

export function DocumentListSkeleton(props: DocumentListSkeletonProps) {
  const { tab, createEnabled = true, releaseId } = props

  const [modalOpen, setModalOpen] = useState(false)

  const showSkeltonActionBar = tab === "upcoming" && releaseId === undefined

  return (
    <Box flexDirection="column" gap={24}>
      {showSkeltonActionBar ? (
        <ActionBarSkeleton />
      ) : (
        <ActionBar
          tab={tab}
          setModalOpen={setModalOpen}
          createEnabled={createEnabled}
          releaseId={releaseId}
        />
      )}
      <DocumentListLoadingTable />

      {createEnabled && !showSkeltonActionBar && (
        <CreateNewDocumentModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          releaseId={tab === "upcoming" && releaseId ? releaseId : undefined}
        />
      )}
    </Box>
  )
}
